/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useMemo } from "react";

import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../viewModel";
import {
  applyMockHostOverride,
  applyVersionOverride,
  resetMockHostOverride,
  resetVersionOverride,
  selectActiveAppVersion,
  selectActiveMockHost,
  selectAppVersionInput,
  selectAppVersionOverrideError,
  selectAppVersionOverrideFocusedIndex,
  selectIsAppVersionOverrideVisible,
  selectMockHostInput,
  selectMockHostOverrideError,
  setFocusedControl,
  setMockHostInput,
  setVersionInput
} from "../../../viewModel/appVersionOverride";
import { useModalController } from "../../hooks/useModalController";
import AppVersionOverlay from "./AppVersionOverlay";
import { AppVersionOverlayController } from "./AppVersionOverlayController";

/**
 * Connected container for the debug-only override overlay (web-app version
 * and mock host).
 *
 * A SINGLE instance is mounted globally in App.tsx, alongside
 * `UnityDialogContainer`. It renders `null` until the hidden `54321` code
 * opens it, so it is invisible during normal usage; in a release build it can
 * never open at all, because the interceptor that dispatches
 * `openAppVersionOverride()` is not installed and the reducer ignores the
 * action even if something else dispatches it.
 *
 * Pushes an `AppVersionOverlayController` onto the active screen's modal stack
 * while open so D-pad input is consumed by the overlay.
 */
const AppVersionOverlayContainer: FunctionComponent =
  (): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
    const TAG: string = "AppVersionOverlayContainer";

    const dispatch = useUnityDispatch();

    const isVisible = useUnitySelector(selectIsAppVersionOverrideVisible);
    const versionInput = useUnitySelector(selectAppVersionInput);
    const mockHostInput = useUnitySelector(selectMockHostInput);
    const activeVersion = useUnitySelector(selectActiveAppVersion);
    const activeMockHost = useUnitySelector(selectActiveMockHost);
    const versionErrorMessage = useUnitySelector(selectAppVersionOverrideError);
    const mockHostErrorMessage = useUnitySelector(selectMockHostOverrideError);
    const focusedIndex = useUnitySelector(selectAppVersionOverrideFocusedIndex);

    const modalController = useMemo(
      (): AppVersionOverlayController =>
        new AppVersionOverlayController(dispatch, UnityAppStore.getState),
      [dispatch]
    );

    useModalController(modalController, isVisible);

    const handleVersionInputChange = useCallback(
      (value: string): void => {
        dispatch(setVersionInput(value));
      },
      [dispatch]
    );

    const handleMockHostInputChange = useCallback(
      (value: string): void => {
        dispatch(setMockHostInput(value));
      },
      [dispatch]
    );

    const handleFocusControl = useCallback(
      (index: number): void => {
        dispatch(setFocusedControl(index));
      },
      [dispatch]
    );

    const handleApplyVersion = useCallback((): void => {
      dispatch(applyVersionOverride());
    }, [dispatch]);

    const handleResetVersion = useCallback((): void => {
      dispatch(resetVersionOverride());
    }, [dispatch]);

    const handleApplyMockHost = useCallback((): void => {
      dispatch(applyMockHostOverride());
    }, [dispatch]);

    const handleResetMockHost = useCallback((): void => {
      dispatch(resetMockHostOverride());
    }, [dispatch]);

    if (isVisible !== true) {
      return null;
    }

    return (
      <AppVersionOverlay
        versionInput={versionInput}
        mockHostInput={mockHostInput}
        activeVersion={activeVersion}
        activeMockHost={activeMockHost}
        versionErrorMessage={versionErrorMessage}
        mockHostErrorMessage={mockHostErrorMessage}
        focusedIndex={focusedIndex}
        onVersionInputChange={handleVersionInputChange}
        onMockHostInputChange={handleMockHostInputChange}
        onFocusControl={handleFocusControl}
        onApplyVersion={handleApplyVersion}
        onResetVersion={handleResetVersion}
        onApplyMockHost={handleApplyMockHost}
        onResetMockHost={handleResetMockHost}
      />
    );
  };

export default AppVersionOverlayContainer;
